import React, { useContext, useEffect, useState, ChangeEvent } from "react";
import { SocketContext } from "@contexts/SocketContext";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import IItem_Option from "@models/IItem_Option";
import INewCatalog_Item_Option from "@models/INewCatalog_Item_Option";
import ICatalog_Item_Option from "@models/ICatalog_Item_Option";
import EndpointsEnum from "@models/EndpointsEnum";
import IUiSocketSetup from "@models/IUiSocketSetup";
import IOnGetCatalogItemOptionsDTO from "@models/DTO/IOnGetCatalogItemOptionsDTO";
import IGetCatalogItemOptionsDTO from "@models/DTO/IGetCatalogItemOptionsDTO";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwButton from "@components/shared/OwButton/OwButton";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import TitleTagEnum from "@models/TitleTagEnum";
import { v4 as uuidv4, v4 } from "uuid";
import "./CatalogItemSidebarItemOptionSection.scss";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import Typeahead from "@components/shared/Typeahead/Typeahead";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import ICatalog_Item_OptionUI from "@models/ICatalog_Item_OptionUI";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";

type Props = {
    catalogId: number;
    catalogGuid: string;
    catalogItemId?: number;
    catalogItemGuid?: string;
    // The currently selected item options for this catalog item
    selectedItemOptions?: ICatalog_Item_Option[];
    // Function to call when item options are updated
    updateItemOptions: (itemOptions: ICatalog_Item_OptionUI[]) => void;
};

interface IItemOptionWithSelection extends IItem_Option {
    isSelected: boolean;
    displayValue?: string;
}

export const CatalogItemSidebarItemOptionSection = (props: Props) => {
    const socket = useContext(SocketContext)!;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const processSelectedOptions = (itemOptions: ICatalog_Item_Option[] | ICatalog_Item_OptionUI[]) => {
        return itemOptions.map((itemOption) => ({
            ...itemOption,
            catalog_item_id: props.catalogItemId || 0,
            uuid: v4(),
        }));
    };

    const [availableItemOptions, setAvailableItemOptions] = useState<IItem_Option[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<ICatalog_Item_OptionUI[]>(
        processSelectedOptions(props.selectedItemOptions || [])
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // const [name, setName] = useState<string | undefined>();
    const [selectedOption, setSelectedOption] = useState<IItem_Option | null>(null);
    const [resetTypeahead, setResetTypeahead] = useState<boolean>(false);
    // Load the available item options from the server
    const onGetCatalogItemOptions = (data: IOnGetCatalogItemOptionsDTO) => {
        setIsLoading(false);

        setAvailableItemOptions(data.itemOptions);
    };

    const getCatalogItemOptions = (name: string) => {
        const data: IGetCatalogItemOptionsDTO = {
            catalogGuid: props.catalogGuid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        if (name) {
            data.name = name;
        }

        socket.emit(EndpointsEnum.CatalogItemOptionGetCatalogItemOptions, data);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogItemOptionOnGetCatalogItemOptions, fn: onGetCatalogItemOptions }];

    useEffect(() => {
        sockets.forEach((item) => {
            socket.on(item.endpoint, item.fn);
        });

        return () => {
            sockets.forEach((item) => {
                socket.off(item.endpoint, item.fn);
            });
        };
    }, []);

    // Update the parent component when the selected options change
    useEffect(() => {
        if (props.updateItemOptions && selectedOptions.length >= 0) {
            props.updateItemOptions(selectedOptions);
        }
    }, [selectedOptions]);

    const setSelectedOptionFn = (option: IItem_Option) => {
        setSelectedOption(option);
    };

    const clearSelectedOption = () => {
        setSelectedOption(null);
        setResetTypeahead(true);
    };

    const resetTypeaheadCallbackFn = () => {
        setSelectedOption(null);
        setResetTypeahead(false);
    };

    const addItemOption = () => {
        if (selectedOption) {
            const newItemOption: ICatalog_Item_OptionUI = {
                item_option_id: selectedOption?.id || 0,
                is_enabled: true,
                display_order: 0,
                uuid: v4(),
                Item_Option: selectedOption,
            };

            setSelectedOptions([...selectedOptions, newItemOption]);

            setSelectedOption(null);
            setResetTypeahead(true);
        }
    };

    const removeItemOption = (itemOption: ICatalog_Item_OptionUI) => {
        // if it's not saved, then remove it, otherwise just toggle the delete flag

        // this doesn't work because the item option itself doesn't have anything indicating that its note saved...
        if (itemOption.item_option_id === 0) {
            const updatedOptions = selectedOptions.filter((option) => option.uuid !== itemOption.uuid);
            setSelectedOptions(updatedOptions);
            props.updateItemOptions(updatedOptions);
        } else {
            const updatedOptions = selectedOptions.map((option) => {
                if (option.uuid === itemOption.uuid) {
                    option.deleteFlag = !option.deleteFlag;
                }
                return option;
            });
            setSelectedOptions(updatedOptions);
            props.updateItemOptions(updatedOptions);
        }
    };

    return (
        <div className="CatalogItemSidebarItemOptionSection">
            <div className={"bd-callout"}>
                <OwLabel title={"item_options"} leftIconClass={"fad fa-list-check"} />
            </div>

            <div className="d-flex w-100 align-items-center input-group">
                <TypeaheadFn
                    wrapperClassName="flex-grow"
                    availableOptions={availableItemOptions}
                    lookupFn={getCatalogItemOptions}
                    selectedOptionFn={setSelectedOptionFn}
                    clearSelectionFn={clearSelectedOption}
                    resetTypeahead={resetTypeahead}
                    resetTypeaheadCallbackFn={resetTypeaheadCallbackFn}
                    titleProp="name"
                    valueProp="id"
                    selectedOption={selectedOption}
                    inputClassName="rounded-end-0"
                />
                <OwButton leftIconClass="fad fa-plus" title="add" disabled={!selectedOption} fn={addItemOption} />
            </div>

            {isLoading ? (
                <div className="text-center py-3">
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    <span>loading_item_options</span>
                </div>
            ) : (
                <>
                    {selectedOptions.length === 0 ? (
                        <div className="alert alert-info mt-2">
                            <i className="fad fa-info-circle me-2"></i>
                            <span>
                                <OwLabel title="no_item_options_selected" />
                            </span>
                        </div>
                    ) : (
                        <div className="item-options-list mt-3">
                            {selectedOptions.map((itemOption) => (
                                <div key={itemOption.item_option_id} className="item-option-row mb-3 border-bottom">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <OwDeleteEditActions
                                                className="me-3"
                                                hideEdit={true}
                                                onEditClickFn={() => {}}
                                                isEditMode={false}
                                                onTrashClickFn={() => removeItemOption(itemOption)}
                                            />
                                            <span className={itemOption.deleteFlag ? "text-decoration-line-through" : ""}>
                                                {itemOption.Item_Option?.name}
                                            </span>
                                        </div>
                                        <small className="text-muted">{itemOption.Item_Option?.type}</small>
                                    </div>

                                    {itemOption.Item_Option?.description && (
                                        <div className="ms-5 mb-2 small text-muted">{itemOption.Item_Option?.description}</div>
                                    )}

                                    <div className="ms-5 mb-2 small text-muted">
                                        {itemOption.Item_Option?.options?.map((option) => option.title).join(", ")}
                                    </div>

                                    {/* {itemOption.isSelected && (
                                        <div className="mt-2 ms-4">
                                            <OwLabel title="value" />
                                            {renderOptionInput(itemOption)}
                                        </div>
                                    )} */}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
